import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { RestorationMaterial } from 'models/order';
import { formatCurrency, formatNumber } from 'utils/format';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';
import TableCellWithHighLighting from 'components/with-hith-light-components/table-cell-with-highlighting.component';

type RestorationMaterialsTableProps = {
    rows: RestorationMaterial[];
    originalRows: RestorationMaterial[];
    currency: string;
    showPrices: boolean;
    onEditRow?: (row: RestorationMaterial, index: number) => void;
    onRemoveRow?: (row: RestorationMaterial, index: number) => void;
    disableDelete?: boolean;
    dataTestPrefix?: string;
};

/**
 * Read-only table over `restoration.materials`. A material added for an action
 * (`relatedActionId`) is deleted with its action and cannot be removed on its own.
 */
const RestorationMaterialsTable: FC<RestorationMaterialsTableProps> = ({
    rows,
    originalRows,
    currency,
    showPrices,
    onEditRow,
    onRemoveRow,
    disableDelete,
    dataTestPrefix = 'restoration-materials',
}) => {
    const { t: translate } = useTranslation(['orders']);
    const hasControls = Boolean(onEditRow || onRemoveRow);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ borderBottom: 'none' }}>
                    <TableRow>
                        <TableCell>{translate('orders:MATERIALS')}</TableCell>
                        <TableCell>{translate('orders:BATCHES')}</TableCell>
                        <TableCell sx={{ width: 50 }} align="right">
                            {translate('orders:QUANTITY')}
                        </TableCell>
                        {showPrices && (
                            <TableCell sx={{ width: 200 }} align="right">
                                {translate('orders:TOTAL_INCL_VAT')}
                            </TableCell>
                        )}
                        {hasControls && <TableCell sx={{ width: 100 }}></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length ? (
                        rows.map((row, index) => {
                            const original = originalRows[index];
                            return (
                                <TableRow key={row._id}>
                                    <TableCell>
                                        <Box display="flex">
                                            <BoxWithHighLighting
                                                mr={1}
                                                data={row.code || ''}
                                                originalData={original?.code || ''}
                                            />
                                            <BoxWithHighLighting
                                                mr={1}
                                                data={row.name}
                                                originalData={original?.name || ''}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCellWithHighLighting
                                        data={row.batchNumber || '-'}
                                        originalData={original?.batchNumber || ''}
                                    />
                                    <TableCellWithHighLighting
                                        align="right"
                                        data={formatNumber(row.price.quantity)}
                                        originalData={original ? formatNumber(original.price.quantity) : ''}
                                    />
                                    {showPrices && (
                                        <TableCellWithHighLighting
                                            align="right"
                                            data={formatCurrency(row.price.totalWithVat, currency)}
                                            originalData={
                                                original ? formatCurrency(original.price.totalWithVat, currency) : ''
                                            }
                                        />
                                    )}
                                    {hasControls && (
                                        <TableCell align="right">
                                            <Fragment>
                                                {onEditRow && !row.isMaterialSurcharge && (
                                                    <IconButton
                                                        onClick={() => onEditRow(row, index)}
                                                        aria-label="edit"
                                                        size="small"
                                                        data-test={`${dataTestPrefix}.edit-material-${index}`}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {onRemoveRow && !row.relatedActionId && !row.isMaterialSurcharge ? (
                                                    <IconButton
                                                        onClick={() => onRemoveRow(row, index)}
                                                        aria-label="delete"
                                                        size="small"
                                                        disabled={disableDelete}
                                                        data-test={`${dataTestPrefix}.delete-material-${index}`}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                ) : undefined}
                                            </Fragment>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8}>
                                <Typography variant="body2" color="#666">
                                    {translate('orders:NO_ADDED_MATERIALS')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default RestorationMaterialsTable;
